import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  Baby,
  BedDouble,
  ChevronRight,
  Clock,
  Dumbbell,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Scale,
  Shirt,
  Sparkles,
  TrendingDown,
  UserRound,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/breast-implant-surgery-mumbai";

const BREAST_FAQS = [
  {
    question: "What is the difference between silicone and saline implants?",
    answer:
      "Silicone implants are filled with cohesive gel that feels closer to natural breast tissue and are the most commonly chosen. Saline implants are filled with sterile salt water after insertion, feel firmer, and deflate visibly if compromised. Both are safe and FDA-approved. Dr. Jain discusses which is appropriate for your anatomy and goals during consultation.",
  },
  {
    question: "Where are the incisions made for breast augmentation?",
    answer:
      "Three incision positions are used: the inframammary fold beneath the breast, the periareolar border around the lower areola, and the axillary fold in the armpit. The inframammary approach is most common as it provides the best access for precise implant placement. Scar placement depends on your anatomy and the implant type selected.",
  },
  {
    question: "Should the implant go above or below the muscle?",
    answer:
      "Placement below the pectoral muscle is recommended for patients with thin breast tissue where implant edges would otherwise be visible or palpable. Placement above the muscle suits patients with adequate existing breast tissue that provides natural coverage. Each placement affects how the implant moves, how it looks in different positions, and long-term mammography assessment. Dr. Jain recommends the appropriate placement based on your specific anatomy.",
  },
  {
    question: "Will breast implants affect breastfeeding?",
    answer:
      "In most cases, no. Implants placed below the muscle and through incisions that do not cross the areola do not interfere with milk ducts or glandular tissue. Patients who are concerned about breastfeeding should discuss incision choice and implant placement during consultation so the plan accounts for this specifically.",
  },
  {
    question: "How long do breast implants last?",
    answer:
      "Modern implants do not have a fixed replacement schedule. They are not designed to be replaced every 10 years as a standard. They are replaced when there is a clinical reason: rupture, capsular contracture, size change, or significant change in breast tissue with age. Many patients go 15 to 20 years without requiring any intervention.",
  },
  {
    question: "What is capsular contracture and how is it prevented?",
    answer:
      "Capsular contracture occurs when the scar tissue that naturally forms around any implant tightens excessively, making the breast feel firm and sometimes distorting its shape. It is the most common complication of breast augmentation. Risk is reduced through precise pocket dissection, implant handling technique, and post-operative management. If it occurs, treatment involves surgical release or implant replacement.",
  },
  {
    question: "Do I need a breast lift alongside implants?",
    answer:
      "This depends on whether breast ptosis, downward displacement of the nipple and breast tissue, is present alongside the volume concern. Implants add volume but do not lift the breast or reposition the nipple. Patients with significant ptosis need a mastopexy combined with augmentation to address both concerns simultaneously.",
  },
  {
    question: "When can I see the final result after breast augmentation?",
    answer:
      "The breast looks close to its intended result within the first 2 to 4 weeks as initial swelling resolves. The implants fully settle into their final position over 3 months as the surrounding tissue adapts and the lower pole of the breast fills out. Final results are assessed at the 3-month post-operative visit.",
  },
];

const breastStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Breast implant surgery (breast augmentation)",
      alternateName: ["Breast augmentation", "Augmentation mammoplasty", "Boob job"],
      description:
        "Surgical placement of silicone or saline breast implants to increase volume, improve shape, and correct asymmetry, with incision and pocket planning tailored to anatomy.",
      procedureType: "Surgical",
      bodyLocation: {
        "@type": "AnatomicalStructure",
        name: "Breast",
      },
      followup:
        "Days 1–3: tightness, swelling. Week 1–2: desk work; avoid heavy lifting. Weeks 4–6: gradual return to exercise. Month 3: implants settled; final assessment.",
      howPerformed:
        "General anesthesia; inframammary, periareolar, or axillary incision; pocket above or below pectoralis muscle; implant insertion and symmetry check; layered closure; surgical bra; 1–2 hours; same-day or overnight discharge.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical" },
        { "@type": "PropertyValue", name: "Typical minimum age", value: "18 years and above" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: BREAST_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

function BreastJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breastStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Breast", href: "/#services" },
    { name: "Breast Implant Surgery", href: "/breast-implant-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Breast</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Breast Implant Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Breast Augmentation by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple" data-testid="hero-badge-certified">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple" data-testid="hero-badge-experience">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple" data-testid="hero-badge-training">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Breast Augmentation" icon="🌸" size="hero"  imageSrc={HERO.breastAugmentation} imageAlt="Breast augmentation"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Breast Augmentation Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Breast augmentation is a surgical procedure that increases breast volume, improves breast shape, and restores
              proportion lost after pregnancy or weight change. It uses silicone or saline implants placed behind the breast
              tissue or chest muscle to create a fuller, more defined breast contour.
            </p>
            <p>
              The concern most patients bring to consultation is not a specific cup size. It is proportion: breasts that feel too
              small relative to their body frame, a chest that reads as flat in clothing, volume that has not returned after
              pregnancy and breastfeeding, or asymmetry significant enough to affect how clothes fit and how the body looks in
              photos. Breast augmentation addresses all of these through implant selection and placement tailored to each
              patient&apos;s anatomy.
            </p>
            <p>
              Dr. Vicky Jain performs breast implant surgery using a systematic approach to implant sizing based on
              chest width, existing breast tissue, and the patient&apos;s body proportions. The goal is a result that looks
              natural to the patient&apos;s frame, not a size that would look out of place on a different body.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain&apos;s Approach to Breast Augmentation
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Implant selection involves four decisions: type, size, profile, and placement. Each affects the final result
                differently. Silicone implants feel closer to natural breast tissue and are the most commonly chosen. Saline
                implants are firmer and have a slightly different feel. Size is selected based on chest width measurement, not
                cup size preference alone. A 300cc implant produces a different result on a narrow chest than on a wider one.
                Profile determines how far the implant projects forward relative to its base width. Placement, either above or
                below the pectoral muscle, affects how the implant behaves and how much coverage the surrounding tissue
                provides.
              </p>
              <p>
                Patients who want a natural result get a different implant recommendation than patients who want a more
                dramatic increase. Both are valid goals. Dr. Jain uses dimensional planning, assessing chest width, skin
                stretch, and existing breast volume, to select the implant that produces the intended result for that specific
                patient&apos;s body. Sizer assessment during consultation allows patients to visualize the change before
                committing to a size.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" data-testid="dr-jain-photo" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatAddressesSection() {
  const cards = [
    {
      title: "Small Breast Size",
      icon: UserRound,
      copy: "Patients with naturally small breasts relative to their body frame choose augmentation to bring the chest into proportion with the rest of their body. Implant size is selected based on chest width and desired outcome, not a generic target.",
    },
    {
      title: "Post-Pregnancy Volume Loss",
      icon: Baby,
      copy: "Pregnancy and breastfeeding frequently reduce breast volume significantly. Some patients lose more volume than they had before pregnancy. Implants restore this volume and, when combined with a lift, restore position as well.",
    },
    {
      title: "Breast Asymmetry",
      icon: Scale,
      copy: "Natural breast asymmetry affects most women to some degree. When the size difference between sides is significant enough to affect clothing fit or body image, different implant sizes on each side correct the asymmetry.",
    },
    {
      title: "Flat or Underdeveloped Chest",
      icon: Layers,
      copy: "Congenital underdevelopment or tuberous breast deformity produces a chest that does not develop normally through puberty. Breast augmentation, sometimes combined with reshaping of the breast base, addresses this structural concern.",
    },
    {
      title: "Poor Clothing and Swimwear Fit",
      icon: Shirt,
      copy: "Many patients describe the practical limitation of small breasts before describing the aesthetic concern: clothing that fits the waist but not the chest, swimwear that does not stay in place, or proportions that do not match their frame. Augmentation resolves the underlying structural cause.",
    },
    {
      title: "Deflation After Weight Loss",
      icon: TrendingDown,
      copy: "Significant weight loss, particularly rapid loss, reduces breast volume and can leave skin without adequate volume underneath it. Implants restore the volume component. When skin laxity is also present, a lift addresses the position.",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="breast-addresses-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Breast Implant Surgery Addresses</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]" data-testid={`breast-addresses-card-${index}`}>
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const IMPLANT_PROCEDURE_TYPES = [
  {
    title: "Silicone Implants",
    body:
      "The most commonly chosen implant type worldwide. Filled with cohesive silicone gel that feels close to natural breast tissue. Available in a range of profiles from low to high, allowing precise control over how much the breast projects forward relative to its base width. Dr. Jain uses form-stable cohesive gel implants that hold their shape even if the outer shell is compromised.",
  },
  {
    title: "Saline Implants",
    body:
      "Filled with sterile salt water after insertion, allowing the incision size to be smaller. Firmer to the touch than silicone. If the shell is compromised, the saline absorbs harmlessly into the body and deflation is immediately visible, which some patients consider an advantage from a safety monitoring perspective.",
  },
  {
    title: "Standard Breast Augmentation",
    body:
      "Implants placed through an incision under the breast crease, around the areola, or through the armpit, depending on anatomy and patient preference. The implant is positioned either above the pectoral muscle for patients with adequate breast tissue coverage, or below it for patients with thin tissue where implant edges would otherwise be visible.",
  },
  {
    title: "Augmentation with Mastopexy (Breast Lift)",
    body:
      "For patients who have volume loss alongside breast descent and skin laxity. Augmentation alone in a ptotic breast produces a result with better volume but unchanged position. Combining a lift with augmentation addresses both concerns simultaneously. The two procedures are planned together to produce a result that is proportionate in both volume and position.",
  },
  {
    title: "Revision Breast Augmentation",
    body:
      "Corrects results from previous implant surgery. Common reasons include capsular contracture, implant displacement, size change, or rupture. Revision surgery is more complex than primary augmentation because existing scar tissue and pocket alterations must be managed alongside the new implant placement.",
  },
];

function ImplantOptionsSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="implant-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Implant Types and Procedure Options</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {IMPLANT_PROCEDURE_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgeryPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Breast Implant Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 1 to 2 hours under general anesthesia. Dr. Jain makes the incision at the chosen site: the
              inframammary fold beneath the breast, the periareolar border around the lower half of the areola, or the axilla
              in the armpit. Each incision position has specific advantages and trade-offs in scar visibility and access. Dr.
              Jain discusses the appropriate incision for your anatomy during consultation.
            </p>
            <p>
              A pocket is created either above or below the pectoral muscle depending on the placement plan. The implant is
              inserted, centered on the chest, and the position is assessed with the patient flat and, when possible, seated
              upright before closure. Symmetry between sides is confirmed before the incisions are closed with layered
              sutures.
            </p>
            <p>
              Patients go home the same day or after one night. A surgical bra is worn immediately after surgery and replaced
              with a supportive bra for the first 6 weeks. Implants take approximately 3 months to settle into their final
              position as the surrounding tissue adapts.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Tightness across the chest, particularly when the implant is placed below the muscle. Swelling and sensitivity are normal. Rest and limited arm movement are recommended.",
      icon: BedDouble,
    },
    {
      period: "Week 1 to 2",
      description:
        "Most patients return to desk work by day 7 to 10. Light daily activity resumes. Avoid lifting anything heavier than 2 kilograms and overhead arm movements.",
      icon: Monitor,
    },
    {
      period: "Weeks 4 to 6",
      description:
        "Exercise and more physically demanding activity resume. The implants are still settling. Upper body exercise begins conservatively before returning to full intensity.",
      icon: Dumbbell,
    },
    {
      period: "Month 3",
      description:
        "Implants reach their final settled position. The breast contour looks and feels natural. Final results are assessed at this visit.",
      icon: Sparkles,
    },
  ];

  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
              Who Is a Good Candidate for Breast Implant Surgery?
            </h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are women aged 18 and above who are in good general health, at a stable weight, and have a
              specific concern with breast size, shape, or volume that affects their body confidence or proportions. Patients
              who are planning significant weight loss should complete that process before surgery, as weight change after
              augmentation affects how the implants look relative to the surrounding tissue.
            </p>
            <p>
              Patients planning future pregnancies should discuss timing with Dr. Jain. Pregnancy after breast augmentation is
              safe, but it can change the breast tissue around the implant and sometimes necessitates revision surgery after
              breastfeeding is complete. Non-smokers or patients willing to stop smoking before and after surgery have
              significantly better healing outcomes. Patients with realistic expectations about what implants change, breast
              volume and shape, and what they do not change, skin laxity, nipple position, or chest wall structure, get the most
              consistent satisfaction from the result.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Breast" />;
}

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BREAST_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10" data-testid={`faq-item-${index}`}>
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Breast Augmentation Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain measures your chest width, assesses your existing breast tissue and skin quality,
          and discusses your proportional goals to determine the implant type, size, profile, and placement that produces the
          right result for your body. You leave with a specific implant recommendation, a clear surgical plan, and a realistic
          understanding of what the procedure involves and what recovery looks like.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Breast Lift",
      description: "Address nipple position and skin laxity alongside volume restoration for a complete result.",
      href: "/breast-lift-surgery-mumbai",
    },
    {
      name: "Liposuction",
      description: "Body contouring to complement improved breast proportion and overall body balance.",
      href: "/liposuction-surgery-mumbai",
    },
    {
      name: "Mommy Makeover",
      description: "Combined post-pregnancy body restoration addressing breast, abdomen, and body contour together.",
      href: "/mommy-makeover-surgery-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
              data-testid={`related-procedure-${procedure.name}`}
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BreastAugmentationPage() {
  return (
    <>
      <SEO
        title="Breast Implant Surgery | Dr. Vicky Jain"
        description="Small breasts, post-pregnancy volume loss, or asymmetry affect confidence and proportion. Dr. Vicky Jain performs breast augmentation. Book your consultation."
        canonical="/breast-implant-surgery-mumbai"
      />
      <BreastJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatAddressesSection />
      <ImplantOptionsSection />
      <HowSurgeryPerformedSection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
